import { useEffect, useState, useCallback, useMemo } from 'react'
import { toast } from 'react-toastify'
import {
  getCompanyGroupAdmins,
  getCompanyAdmins,
  getVesselList,
  getManagementUsers,
  getVendors,
  getAssignableRolesForUserCreate,
} from '../../../core/_requests'
import { getApiErrorMessage, showErrorToast } from '../../../../../shared/utils/apiErrorMessage'
import type { ManagementDashboardData, ManagementDerivedKpis } from '../types'

export interface UseManagementDashboardReturn {
  loading: boolean
  error: string | null
  hasError: boolean
  failedSections: string[]
  data: ManagementDashboardData
  kpis: ManagementDerivedKpis
  refresh: () => void
}

export function useManagementDashboard(): UseManagementDashboardReturn {
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [failedSections, setFailedSections] = useState<string[]>([])
  const [data, setData] = useState<ManagementDashboardData>({
    companies: [],
    subCompanies: [],
    vessels: [],
    users: [],
    vendors: [],
    roles: [],
  })

  const fetchAll = useCallback(async () => {
    setLoading(true)
    setError(null)
    setFailedSections([])

    try {
      const endpoints = [
        { label: 'Companies', promise: getCompanyGroupAdmins() },
        { label: 'Sub-Companies', promise: getCompanyAdmins() },
        { label: 'Vessels', promise: getVesselList() },
        { label: 'Users', promise: getManagementUsers() },
        { label: 'Vendors', promise: getVendors() },
        { label: 'Roles', promise: getAssignableRolesForUserCreate() },
      ] as const

      const results = await Promise.allSettled(endpoints.map((e) => e.promise))

      const failed: string[] = []
      let firstError: unknown = null

      results.forEach((r, idx) => {
        if (r.status === 'rejected') {
          failed.push(endpoints[idx].label)
          if (!firstError) {
            firstError = r.reason
          }
        }
      })

      const [companies, subCompanies, vessels, users, vendors, roles] = results.map(
        (r) => (r.status === 'fulfilled' ? r.value : [])
      )

      setData({
        companies: Array.isArray(companies) ? companies : [],
        subCompanies: Array.isArray(subCompanies) ? subCompanies : [],
        vessels: Array.isArray(vessels) ? vessels : [],
        users: Array.isArray(users) ? users : [],
        vendors: Array.isArray(vendors) ? vendors : [],
        roles: Array.isArray(roles) ? roles : [],
      })

      if (failed.length > 0) {
        setFailedSections(failed)
        if (failed.length === endpoints.length) {
          const msg = getApiErrorMessage(firstError, 'Failed to load dashboard data. Please check your network connection.')
          setError(msg)
          showErrorToast(firstError, 'Failed to load dashboard data.')
        } else {
          const detailMsg = `Some dashboard sections failed to load (${failed.join(', ')}). Available data is shown.`
          setError(detailMsg)
          toast.warn(detailMsg)
        }
      } else {
        setError(null)
        setFailedSections([])
      }
    } catch (err) {
      const msg = getApiErrorMessage(err, 'Failed to load dashboard data.')
      setError(msg)
      showErrorToast(err, 'Failed to load dashboard data.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchAll()
  }, [fetchAll])

  const kpis: ManagementDerivedKpis = useMemo(() => ({
    totalCompanies: data.companies.length,
    totalSubCompanies: data.subCompanies.length,
    totalVessels: data.vessels.length,
    totalUsers: data.users.length,
    activeUsers: data.users.filter((u) => u.enabled).length,
    totalVendors: data.vendors.length,
    totalRoles: data.roles.length,
  }), [data])

  return {
    loading,
    error,
    hasError: Boolean(error) || failedSections.length > 0,
    failedSections,
    data,
    kpis,
    refresh: fetchAll,
  }
}
